"use client";
import {
  makeStyles,
  tokens,
  Button,
  shorthands,
} from "@fluentui/react-components";
import { useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import ErrorFallbackComponent from "../components/fallback/ErrorFallbackComponent";
import { useNavigate } from "react-router-dom";
import { ClientRoutes } from "../models/ClientRoutes";
import { SkanskaColorTokens } from "../style/colors";
import { Breakpoints } from "../style/styles";
import { useTranslation } from "react-i18next";
import { useGetUserAccessLevel } from "../hooks/useGetUserAccessLevel";

const useClasses = makeStyles({
  root: {
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
    padding: tokens.spacingVerticalM,
  },
  adminTitle: {
    textAlign: "center",
    marginBottom: "20px",
  },
  container: {
    display: "grid",
    flexDirection: "column",
    gap: "40px",
    gridTemplateColumns: "1fr 1fr 1fr",
    ...Breakpoints.medium({
      gridTemplateColumns: "1fr",
    }),
  },
  adminInfo: {
    backgroundColor: SkanskaColorTokens.color_secondary_dim_white,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    paddingBottom: "15px",
    padding: tokens.spacingVerticalL,
  },
  adminInfoDanger: {
    backgroundColor: SkanskaColorTokens.color_tertiary_red_15,
    ...shorthands.border("2px", "solid", SkanskaColorTokens.color_tertiary_red),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    paddingBottom: "15px",
    padding: tokens.spacingVerticalL,
  },
});

function StartPage() {
  const classes = useClasses();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const accessLevel = useGetUserAccessLevel();

  useEffect(() => {
    // Send endusers and low end improvement managers to the enduser client.
    if (accessLevel > -1 && accessLevel < 2) {
      window.location.assign(import.meta.env.VITE_CLIENT_APP_URL);
    }
  }, [accessLevel]);

  return (
    <div className={classes.root}>
      <h1 className={classes.adminTitle}>{t("welcomeVsaAdmin")}</h1>
      <ErrorBoundary FallbackComponent={ErrorFallbackComponent}>
        <div className={classes.container}>
          {accessLevel >= 3 && (
            <div className={classes.adminInfo}>
              <h2>{t("tagManager")}</h2>
              <p>{t("tagPageDescription")}</p>
              <Button onClick={() => navigate(ClientRoutes.TagManager)}>
                {t("tagPageLink")}
              </Button>
            </div>
          )}

          {accessLevel >= 3 && (
            <div className={classes.adminInfo}>
              <h2>{t("activities")}</h2>
              <p>{t("activityPageDescription")}</p>
              <Button
                onClick={() =>
                  navigate(
                    ClientRoutes.Activities + "?sortBy=modified&sortDir=desc"
                  )
                }
              >
                {t("activityPageLink")}
              </Button>
            </div>
          )}
          {accessLevel >= 4 && (
            <div className={classes.adminInfo}>
              <h2>{t("chapter")}</h2>
              <p>{t("chapterPageDescription")}</p>
              <Button onClick={() => navigate(ClientRoutes.Chapters)}>
                {t("chapterPageLink")}
              </Button>
            </div>
          )}
          {accessLevel >= 3 && (
            <div className={classes.adminInfo}>
              <h2>{t("document")}</h2>
              <p>{t("documentsPageDescription")}</p>
              <Button onClick={() => navigate(ClientRoutes.Documents)}>
                {t("documentsPageLink")}
              </Button>
            </div>
          )}
          {accessLevel >= 3 && (
            <div className={classes.adminInfo}>
              <h2>{t("links")}</h2>
              <p>{t("linksPageDescription")}</p>
              <Button onClick={() => navigate(ClientRoutes.Links)}>
                {t("linksPageLink")}
              </Button>
            </div>
          )}
          {accessLevel >= 3 && (
            <div className={classes.adminInfo}>
              <h2>{t("roles")}</h2>
              <p>{t("rolesPageDescription")}</p>
              <Button onClick={() => navigate(ClientRoutes.Roles)}>
                {t("rolesPageLink")}
              </Button>
            </div>
          )}
          {accessLevel >= 3 && (
            <div className={classes.adminInfo}>
              <h2>{t("media")}</h2>
              <p>{t("mediaPageDescription")}</p>
              <Button onClick={() => navigate(ClientRoutes.Media)}>
                {t("mediaPageLink")}
              </Button>
            </div>
          )}
          {accessLevel >= 3 && (
            <div className={classes.adminInfo}>
              <h2>{t("systemInfoBanner.title")}</h2>
              <p>{t("systemInfoBanner.description")}</p>
              <Button onClick={() => navigate(ClientRoutes.SystemInfoBanner)}>
                {t("systemInfoBanner.manage")}
              </Button>
            </div>
          )}
          {accessLevel >= 4 && (
            <div className={classes.adminInfo}>
              <h2>{t("entraGroupMembership.title")}</h2>
              <p>{t("entraGroupMembership.description")}</p>
              <Button
                onClick={() => navigate(ClientRoutes.EntraGroupMemberships)}
              >
                {t("entraGroupMembership.manage")}
              </Button>
            </div>
          )}
          {accessLevel >= 2 && (
            <div className={classes.adminInfo}>
              <h2>{t("reportsPage.title")}</h2>
              <p>{t("reportsPage.description")}</p>
              <Button onClick={() => navigate(ClientRoutes.Reports)}>
                {t("reportsPage.link")}
              </Button>
            </div>
          )}
          {accessLevel >= 5 && (
            <div className={classes.adminInfoDanger}>
              <h2>{t("systemAdminPage")}</h2>
              <p>{t("systemAdminPageDescription")}</p>
              <Button onClick={() => navigate(ClientRoutes.SystemAdmin)}>
                {t("admin")}
              </Button>
            </div>
          )}
        </div>
      </ErrorBoundary>
    </div>
  );
}

export default StartPage;
